import React, { useEffect, useState } from 'react';
import {
  Box, AppBar, Toolbar, IconButton, Typography, Container,
  Card, CardContent, Button, Stack, Chip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
  List, ListItem, ListItemText, ListItemSecondaryAction,
  Snackbar, Tooltip,
} from '@mui/material';
import {
  ArrowBack, Add, Key, Delete, AdminPanelSettings, Person,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usersAPI } from '../services/api';

interface UserRow {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'erfasser';
  created_at: string;
  last_login_at: string | null;
}

export default function Users() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);

  // Anlegen-Dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', name: '', password: '', role: 'erfasser' });
  const [busy, setBusy] = useState(false);

  // Passwort-Reset-Dialog
  const [pwResetUser, setPwResetUser] = useState<UserRow | null>(null);
  const [newPassword, setNewPassword] = useState('');

  // Lösch-Bestätigung
  const [deleteUser, setDeleteUser] = useState<UserRow | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const list = await usersAPI.list();
      setUsers(list);
    } catch (e: any) {
      setError(e.response?.data?.error || 'User-Liste fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Schutz: nur Admin
    if (currentUser && currentUser.role !== 'admin') {
      navigate('/');
      return;
    }
    load();
  }, [currentUser]);

  const handleCreate = async () => {
    if (!createForm.email || !createForm.name || !createForm.password) {
      setSnackbar({ text: 'Alle Felder erforderlich', severity: 'error' });
      return;
    }
    if (createForm.password.length < 8) {
      setSnackbar({ text: 'Passwort min. 8 Zeichen', severity: 'error' });
      return;
    }
    setBusy(true);
    try {
      await usersAPI.create(createForm);
      setSnackbar({ text: `User ${createForm.email} angelegt`, severity: 'success' });
      setCreateOpen(false);
      setCreateForm({ email: '', name: '', password: '', role: 'erfasser' });
      await load();
    } catch (e: any) {
      setSnackbar({ text: e.response?.data?.error || 'Anlage fehlgeschlagen', severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!pwResetUser || newPassword.length < 8) {
      setSnackbar({ text: 'Passwort min. 8 Zeichen', severity: 'error' });
      return;
    }
    try {
      await usersAPI.resetPassword(pwResetUser.id, newPassword);
      setSnackbar({ text: `Passwort für ${pwResetUser.email} zurückgesetzt`, severity: 'success' });
      setPwResetUser(null);
      setNewPassword('');
    } catch (e: any) {
      setSnackbar({ text: e.response?.data?.error || 'Reset fehlgeschlagen', severity: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    try {
      await usersAPI.delete(deleteUser.id);
      setSnackbar({ text: `User ${deleteUser.email} gelöscht`, severity: 'success' });
      setDeleteUser(null);
      await load();
    } catch (e: any) {
      setSnackbar({ text: e.response?.data?.error || 'Löschen fehlgeschlagen', severity: 'error' });
    }
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <AppBar position="sticky" elevation={1}>
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate('/')} edge="start">
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700, ml: 1 }}>
            Benutzer-Verwaltung
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={700}>
            {users.length} Benutzer
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
            Neuer Benutzer
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Card>
          <List>
            {users.map((u, i) => (
              <React.Fragment key={u.id}>
                {i > 0 && <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}
                <ListItem>
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {u.role === 'admin'
                          ? <AdminPanelSettings fontSize="small" color="primary" />
                          : <Person fontSize="small" />}
                        <Typography fontWeight={600}>{u.name}</Typography>
                        {u.id === currentUser?.id && (
                          <Chip size="small" label="du" color="primary" variant="outlined" />
                        )}
                        <Chip
                          size="small"
                          label={u.role}
                          color={u.role === 'admin' ? 'primary' : 'default'}
                        />
                      </Stack>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" component="span" color="text.secondary">
                          {u.email}
                        </Typography>
                        <Typography variant="caption" component="div" color="text.secondary">
                          Erstellt: {new Date(u.created_at).toLocaleDateString('de-DE')} ·{' '}
                          Letzter Login: {u.last_login_at
                            ? new Date(u.last_login_at).toLocaleString('de-DE')
                            : 'noch nie'}
                        </Typography>
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Passwort zurücksetzen">
                      <IconButton onClick={() => setPwResetUser(u)}>
                        <Key />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Löschen">
                      <span>
                        <IconButton
                          onClick={() => setDeleteUser(u)}
                          disabled={u.id === currentUser?.id}
                        >
                          <Delete />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
            {!loading && users.length === 0 && (
              <ListItem>
                <ListItemText primary="Keine Benutzer vorhanden" />
              </ListItem>
            )}
          </List>
        </Card>
      </Container>

      {/* Neuer-User-Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Neuen Benutzer anlegen</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              fullWidth size="small"
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              autoFocus
            />
            <TextField
              label="E-Mail"
              type="email" fullWidth size="small"
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
            />
            <TextField
              label="Passwort"
              type="password" fullWidth size="small"
              value={createForm.password}
              onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
              helperText="Mindestens 8 Zeichen"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Rolle</InputLabel>
              <Select
                label="Rolle"
                value={createForm.role}
                onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
              >
                <MenuItem value="erfasser">Erfasser</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleCreate} disabled={busy}>
            Anlegen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Passwort-Reset-Dialog */}
      <Dialog open={!!pwResetUser} onClose={() => setPwResetUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Passwort zurücksetzen</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Setze ein neues Passwort für <strong>{pwResetUser?.email}</strong>.
            Der Benutzer kann sich danach mit diesem Passwort anmelden.
          </Typography>
          <TextField
            label="Neues Passwort"
            type="password" fullWidth size="small"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText="Mindestens 8 Zeichen"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwResetUser(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={handlePasswordReset}>
            Zurücksetzen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lösch-Bestätigung */}
      <Dialog open={!!deleteUser} onClose={() => setDeleteUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Benutzer löschen?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>{deleteUser?.email}</strong> wird unwiderruflich gelöscht.
            Erfasste Bäume bleiben erhalten, sind aber dann keinem Benutzer mehr zugeordnet.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUser(null)}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Endgültig löschen
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {snackbar ? <Alert severity={snackbar.severity}>{snackbar.text}</Alert> : <></>}
      </Snackbar>
    </Box>
  );
}
